type AllowedScriptSource = {
  origin: string
  pathname: string
}

type TrustedScriptPolicy = {
  createScript: (value: string) => string
  createScriptURL: (value: string) => string
}

type TrustedTypesGlobal = {
  createPolicy: (
    name: string,
    rules: {
      createScript: (value: string) => string
      createScriptURL: (value: string) => string
    },
  ) => TrustedScriptPolicy
}

const allowedScriptSources: AllowedScriptSource[] = [
  { origin: 'https://www.googletagmanager.com', pathname: '/gtm.js' },
  { origin: 'https://www.googletagmanager.com', pathname: '/gtag/js' },
  { origin: 'https://connect.facebook.net', pathname: '/en_US/fbevents.js' },
]
const schemaOrgContext = 'https://schema.org'

let trustedScriptPolicy: TrustedScriptPolicy | null | undefined

function getTrustedTypes(): TrustedTypesGlobal | undefined {
  return (globalThis as { trustedTypes?: TrustedTypesGlobal }).trustedTypes
}

function hasSchemaOrgContext(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some((item) => hasSchemaOrgContext(item))
  }

  if (!value || typeof value !== 'object') {
    return false
  }

  const record = value as Record<string, unknown>
  const context = record['@context']

  if (context === schemaOrgContext) {
    return true
  }

  if (Array.isArray(context) && context.includes(schemaOrgContext)) {
    return true
  }

  return hasSchemaOrgContext(record['@graph'])
}

function createSafeScriptUrl(value: string) {
  const url = new URL(value, window.location.href)
  const isAllowed = allowedScriptSources.some((source) =>
    url.origin === source.origin && url.pathname === source.pathname
  )

  if (!isAllowed) {
    throw new Error(`Blocked script URL: ${url.origin}${url.pathname}`)
  }

  return url.toString()
}

function createSafeJsonLdScript(value: string) {
  const parsed = JSON.parse(value) as unknown

  if (!hasSchemaOrgContext(parsed)) {
    throw new TypeError('Blocked script content: JSON-LD must include a schema.org @context.')
  }

  return value
}

function getTrustedScriptPolicy() {
  const trustedTypes = getTrustedTypes()

  if (!trustedTypes) {
    return null
  }

  if (trustedScriptPolicy !== undefined) {
    return trustedScriptPolicy
  }

  trustedScriptPolicy = trustedTypes.createPolicy('vue', {
    createScript: createSafeJsonLdScript,
    createScriptURL: createSafeScriptUrl,
  })

  return trustedScriptPolicy
}

export function createTrustedJsonLdScript(value: string) {
  const policy = getTrustedScriptPolicy()

  return policy ? policy.createScript(value) : value
}

export function setTrustedScriptSrc(script: HTMLScriptElement, value: string) {
  const policy = getTrustedScriptPolicy()
  const trustedValue = policy ? policy.createScriptURL(value) : value

  script.src = trustedValue
}
